import { useDispatch } from 'react-redux';
import { loginUser, loginWithCyberArkUrlToken } from '../redux/user/user.action';
import { useFormik, Form, Field, FormikProvider } from 'formik';
import { Button, Input } from 'styled';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { setCurrentUser } from '../redux/user/userSlice';
import toast from 'react-hot-toast';
import axios from 'apis/axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
// import {setCurrentUser} from 'redux/user/user.action'

/** Prevents duplicate SSO attempts (e.g. React Strict Mode double mount) for the same URL token. */
let cyberArkUrlTokenHandled = null;

const PHONE_REGEX = /^[6-9]\d{9}$/;
const OTP_REGEX = /^\d{6}$/;

const sanitizeDigits = (value, maxLen) => (value || '').replace(/\D/g, '').slice(0, maxLen);

/** Mirrors the password policy used by the backend and change-password screen. */
const validatePasswordPolicy = (password) => {
  if (!password || password.length < 12) {
    return 'Password must be at least 12 characters long';
  }
  const categories = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/];
  const matched = categories.filter((re) => re.test(password)).length;
  if (matched < 3) {
    return 'Password must include at least 3 of: uppercase, lowercase, number, special character';
  }
  return null;
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [source, setSource] = useState("/dashboard")
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [resetPasswordContext, setResetPasswordContext] = useState(null);
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  const skipStoredUserRedirectRef = useRef(false);

  useEffect(()=>{
    let url = new URL(window.location.href);
    let params = new URLSearchParams(url.search);
    params.delete("source");
    params.delete("token");
    const paramString = params.toString();
    //console.log(searchParams.get("source") + "?" + (paramString ?? ""))
    setSource(((searchParams.get("source") ?? "/dashboard" )+ "?" + (paramString ?? "")) || "/dashboard")
  }, [searchParams])

  useEffect(() => {
    const urlToken = searchParams.get('token')?.trim();
    if (!urlToken) return;
    if (cyberArkUrlTokenHandled === urlToken) return;
    cyberArkUrlTokenHandled = urlToken;
    skipStoredUserRedirectRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const sourceBase = searchParams.get('source') ?? '/dashboard';
    params.delete('source');
    params.delete('token');
    const rest = params.toString();
    const redirect = rest ? `${sourceBase}?${rest}` : sourceBase;

    dispatch(loginWithCyberArkUrlToken(urlToken, redirect, setIsLoading)).then((ok) => {
      if (ok === false) {
        cyberArkUrlTokenHandled = null;
        skipStoredUserRedirectRef.current = false;
        const next = new URLSearchParams(searchParams);
        next.delete('token');
        const qs = next.toString();
        navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
      }
    });
  }, [searchParams, dispatch, navigate, location.pathname]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: '',
      pin: '',
      fromDashboard: true,
    },
    onSubmit: async (values) => {
      //console.log({source})
        dispatch(loginUser(values, navigate, source, setIsLoading));
        setIsLoggedIn(true)
    },

    validate: (values) => {
      let errors = {};
      if (!values.email){
        errors.email = 'Required'
      }
      else if(!(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(values.email))){
        errors.email = "Invalid User ID format"
      }

      if (!values.pin) errors.pin = 'Required';
      return errors;
    },
  });

  const forgotPasswordFormik = useFormik({
    initialValues: {
      phone: '',
      otp: '',
    },
    onSubmit: async (values) => {
      if (otpSent) {
        await verifyForgotPasswordOtp(values);
      } else {
        await sendForgotPasswordOtp(values);
      }
    },
    validate: (values) => {
      const errors = {};
      if (!values.phone) {
        errors.phone = 'Mobile number is required';
      } else if (!PHONE_REGEX.test(values.phone)) {
        errors.phone = 'Enter a valid 10-digit mobile number';
      }

      if (otpSent) {
        if (!values.otp) {
          errors.otp = 'OTP is required';
        } else if (!OTP_REGEX.test(values.otp)) {
          errors.otp = 'Enter a valid 6-digit OTP';
        }
      }

      return errors;
    },
  });

  const resetPasswordFormik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    onSubmit: async (values) => {
      if (!resetPasswordContext?.userId || !resetPasswordContext?.resetPasswordToken) {
        toast.error('OTP verification is required');
        return;
      }

      setForgotPasswordLoading(true);
      try {
        await axios.put(
          `user/update-password/${resetPasswordContext.userId}`,
          {
            pin: values.password,
            resetPasswordToken: resetPasswordContext.resetPasswordToken,
          },
          { headers: { Authorization: '' } }
        );

        toast.success('Password changed successfully');
        closeForgotPassword();
      } catch (err) {
        // Backend messages already say "password", so they pass through as-is.
        toast.error(err.response?.data?.message || err.message || 'Could not change the password');
      } finally {
        setForgotPasswordLoading(false);
      }
    },
    validate: (values) => {
      const errors = {};
      const policyError = validatePasswordPolicy(values.password);

      if (!values.password) {
        errors.password = 'Required';
      } else if (policyError) {
        errors.password = policyError;
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = 'Required';
      } else if (values.confirmPassword !== values.password) {
        errors.confirmPassword = 'Passwords do not match';
      }

      return errors;
    },
  });

  async function sendForgotPasswordOtp(values) {
    setForgotPasswordLoading(true);
    try {
      const { data } = await axios.post(
        'user/send-otp',
        { phone: values.phone },
        { headers: { Authorization: '' } }
      );
      toast.success(data?.message || 'OTP sent successfully');
      setOtpSent(true);
      forgotPasswordFormik.setFieldValue('otp', '');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not send OTP');
    } finally {
      setForgotPasswordLoading(false);
    }
  }

  async function verifyForgotPasswordOtp(values) {
    setForgotPasswordLoading(true);
    try {
      const { data } = await axios.post(
        'user/verify-otp',
        {
          phone: values.phone,
          otp: values.otp,
        },
        { headers: { Authorization: '' } }
      );

      setResetPasswordContext({
        userId: data?.userId,
        resetPasswordToken: data?.resetPasswordToken,
      });
      resetPasswordFormik.resetForm();
      setForgotPasswordStep('reset');
      toast.success(data?.message || 'OTP verified successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'OTP verification failed');
    } finally {
      setForgotPasswordLoading(false);
    }
  }

  function openForgotPassword() {
    setForgotPasswordStep('verify');
    setOtpSent(false);
    setResetPasswordContext(null);
    setShowForgotNewPassword(false);
    setShowForgotConfirmPassword(false);
    forgotPasswordFormik.resetForm();
    resetPasswordFormik.resetForm();
  }

  function closeForgotPassword() {
    setForgotPasswordStep(null);
    setOtpSent(false);
    setResetPasswordContext(null);
    setShowForgotNewPassword(false);
    setShowForgotConfirmPassword(false);
    forgotPasswordFormik.resetForm();
    resetPasswordFormik.resetForm();
  }

  useEffect(()=>{
    if (skipStoredUserRedirectRef.current) return;
    const rawUser = localStorage.getItem("user");
    const user = rawUser ? JSON.parse(rawUser) : null;
    const token = localStorage.getItem("token");
    if(user && Object.keys(user).length!== 0){
      dispatch(setCurrentUser({...user, token: token}, navigate));
      navigate(source)
    }
  }, [isLoggedIn, searchParams, dispatch, navigate, source])
  return (
    <div
      className="flex justify-center items-center h-screen w-screen "
      style={{
        backgroundImage: `url(bg.png)`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundBlendMode: 'multiply',
        backgroundColor: 'hsl(200, 100%, 30%)',
      }}
    >
      <FormikProvider value={formik}>
        <Form className="mx-4 sm:mx-0 w-full sm:w-1/2 lg:w-1/3 relative bg-white bg-opacity-40  rounded-md flex flex-col justify-center items-center">
          {searchParams.get('token') && isLoading && (
            <p className="bg-blue-600 p-3 absolute w-full rounded-md text-center top-0 text-white z-10">
              Signing you in…
            </p>
          )}
          {formik.touched.email ||
            (formik.touched.email && formik.errors && (
              <p className="bg-yellow-400 p-3 absolute w-full rounded-md text-center top-0 text-white">
                Please enter the User ID and Password
              </p>
            ))}
          <div className="p-4 2xl:p-6 flex flex-col w-full space-y-3 justify-center items-center text-gray-800">
            <label htmlFor="userId" className="w-full">
              User ID
            </label>
            <Field as={Input} type="text" name="email" id="userId" placeholder="Enter User ID" />
            {formik.touched.email && formik.errors ? <div className="flex items-start w-full"><strong className="text-red-500 ">{formik.errors.email}</strong></div>:""}
            {/* name="pin" is the backend's login payload key — label only. */}
            <label htmlFor="pin" className="w-full">
              Password
            </label>
            <Field as={Input} type="password" name="pin" id="pin" />
            {formik.touched.pin && formik.errors ? <div className="flex items-start w-full"><strong className="text-red-500 ">{formik.errors.pin}</strong></div>:""}
            <button
              type="button"
              className="w-full text-right text-sm font-semibold text-blue-700 hover:text-blue-900"
              onClick={openForgotPassword}
            >
              Forgot Password?
            </button>
              <Button
              id="submitButton"
              className={`login_submit__-r5zj bg-[#FDBC00] ${!isLoading && 'hover:bg-green-700'} text-white p-2 rounded-md w-1/2`}
              style={{marginTop: '1.25rem'}}
              onClick={formik.handleSubmit}
              disabled={isLoading}
            >
              Log In
            </Button>
          </div>
        </Form>
      </FormikProvider>
      {forgotPasswordStep === 'verify' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <FormikProvider value={forgotPasswordFormik}>
            <Form className="w-full max-w-md rounded-md bg-white p-5 shadow-xl">
              <div className="flex w-full flex-col space-y-3 text-gray-800">
                <h2 className="text-xl font-semibold">Forgot Password</h2>
                <p className="text-sm text-gray-700">
                  Enter your registered mobile number to receive an OTP on SMS and email.
                </p>

                <label htmlFor="forgotPhone" className="w-full">
                  Mobile Number
                </label>
                <Field
                  as={Input}
                  type="tel"
                  name="phone"
                  id="forgotPhone"
                  placeholder="Enter mobile number"
                  value={forgotPasswordFormik.values.phone}
                  onChange={(event) =>
                    forgotPasswordFormik.setFieldValue('phone', sanitizeDigits(event.target.value, 10))
                  }
                  disabled={otpSent || forgotPasswordLoading}
                  autoComplete="tel"
                />
                {forgotPasswordFormik.touched.phone && forgotPasswordFormik.errors.phone ? (
                  <div className="flex items-start w-full">
                    <strong className="text-red-500">{forgotPasswordFormik.errors.phone}</strong>
                  </div>
                ) : (
                  ''
                )}

                {otpSent && (
                  <>
                    <label htmlFor="forgotOtp" className="w-full">
                      OTP
                    </label>
                    <Field
                      as={Input}
                      type="text"
                      name="otp"
                      id="forgotOtp"
                      placeholder="Enter OTP"
                      value={forgotPasswordFormik.values.otp}
                      onChange={(event) =>
                        forgotPasswordFormik.setFieldValue('otp', sanitizeDigits(event.target.value, 6))
                      }
                      disabled={forgotPasswordLoading}
                      autoComplete="one-time-code"
                    />
                    {forgotPasswordFormik.touched.otp && forgotPasswordFormik.errors.otp ? (
                      <div className="flex items-start w-full">
                        <strong className="text-red-500">{forgotPasswordFormik.errors.otp}</strong>
                      </div>
                    ) : (
                      ''
                    )}
                  </>
                )}

                <div className="flex w-full justify-end gap-3 pt-3">
                  <button
                    type="button"
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                    onClick={closeForgotPassword}
                    disabled={forgotPasswordLoading}
                  >
                    Cancel
                  </button>
                  <Button
                    type="submit"
                    className="bg-[#FDBC00] text-white"
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? 'Please wait...' : otpSent ? 'Verify OTP' : 'Send OTP'}
                  </Button>
                </div>
              </div>
            </Form>
          </FormikProvider>
        </div>
      )}
      {forgotPasswordStep === 'reset' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <FormikProvider value={resetPasswordFormik}>
            <Form className="w-full max-w-md rounded-md bg-white p-5 shadow-xl">
              <div className="flex w-full flex-col space-y-3 text-gray-800">
                <h2 className="text-xl font-semibold">Set New Password</h2>
                <p className="text-sm text-gray-700">
                  Create a new password for your account.
                </p>

                <label htmlFor="forgotNewPassword" className="w-full">
                  New Password
                </label>
                <div className="relative w-full">
                  <Field
                    as={Input}
                    type={showForgotNewPassword ? 'text' : 'password'}
                    name="password"
                    id="forgotNewPassword"
                    placeholder="Enter new password"
                    className="pr-10"
                    disabled={forgotPasswordLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-gray-600"
                    onClick={() => setShowForgotNewPassword((show) => !show)}
                    disabled={forgotPasswordLoading}
                    aria-label={showForgotNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    {showForgotNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {resetPasswordFormik.touched.password && resetPasswordFormik.errors.password ? (
                  <div className="flex items-start w-full">
                    <strong className="text-red-500">{resetPasswordFormik.errors.password}</strong>
                  </div>
                ) : (
                  ''
                )}

                <label htmlFor="forgotConfirmPassword" className="w-full">
                  Confirm New Password
                </label>
                <div className="relative w-full">
                  <Field
                    as={Input}
                    type={showForgotConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    id="forgotConfirmPassword"
                    placeholder="Re-enter new password"
                    className="pr-10"
                    disabled={forgotPasswordLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-gray-600"
                    onClick={() => setShowForgotConfirmPassword((show) => !show)}
                    disabled={forgotPasswordLoading}
                    aria-label={showForgotConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showForgotConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {resetPasswordFormik.touched.confirmPassword && resetPasswordFormik.errors.confirmPassword ? (
                  <div className="flex items-start w-full">
                    <strong className="text-red-500">{resetPasswordFormik.errors.confirmPassword}</strong>
                  </div>
                ) : (
                  ''
                )}

                <p className="w-full text-xs text-gray-700">
                  Password must use at least 12 characters with a mix of uppercase, lowercase, numbers and symbols
                  (any 3 of these).
                </p>

                <div className="flex w-full justify-end gap-3 pt-3">
                  <button
                    type="button"
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                    onClick={closeForgotPassword}
                    disabled={forgotPasswordLoading}
                  >
                    Cancel
                  </button>
                  <Button
                    type="submit"
                    className="bg-[#FDBC00] text-white"
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? 'Saving...' : 'Change Password'}
                  </Button>
                </div>
              </div>
            </Form>
          </FormikProvider>
        </div>
      )}
    </div>
  );
};

export default Login;
